import React, { useEffect, useRef, useState } from 'react'
import { useAuth0 } from '../../../../contexts/auth0-context'
import { searchPubMedIds, buildPubMedQuery } from './PubMedUtilities'
import { pubmedIndicationConfig } from './pubmedIndicationConfig'
import { PubMedSearchParams, PubMedSearchArticleTypes } from './types'
import {
    Grid,
    TextField,
    Typography,
    Button,
    MenuItem,
    ListItemText,
    Checkbox,
    FormControl,
    FormControlLabel,
    Select,
    OutlinedInput,
    InputLabel,
    CircularProgress,
    Tooltip,
    IconButton,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'

interface PubMedSearchProps {
    // Called after esearch completes with the ordered PMID list and the params used.
    // The parent stores these IDs and fetches articles page-by-page via fetchPubMedArticles.
    onSearchComplete: (ids: string[], params: PubMedSearchParams) => void

    onLoadingChange?: (loading: boolean) => void

    // True while the parent is still fetching/ranking articles from a prior search.
    // Used to keep the Search button disabled for the full search+ranking duration,
    // not just the initial esearch call.
    disabled?: boolean

    initialDiseaseArea?: string
    initialInterventions?: string
}

function normalizeSearchLabel(value: string | undefined): string {
    return (value || '')
        .replace(/\s*\([A-Za-z0-9\-]+\)\s*$/, '')
        .trim()
        .toLowerCase()
}

function joinTerms(terms: string[]): string {
    return Array.from(new Set(terms.filter(Boolean))).join(', ')
}

/**
 * Fallback handling for roadmap indications that include an acronym in parentheses.
 *
 * Example:
 *   Drug Resistant Tuberculosis (DR-TB)
 *
 * becomes:
 *   ['Drug Resistant Tuberculosis', 'DR-TB']
 *
 * This allows the query builder to generate:
 *   "Drug Resistant Tuberculosis"[tiab] OR "DR-TB"[tiab]
 *
 * without requiring every disease/acronym combination to be manually
 * maintained in pubmedIndicationConfig.
 *
 * If an indication exists in pubmedIndicationConfig, the configured
 * defaultDiseaseTerms will still take precedence.
 */

function splitLabelWithParentheticalAcronym(value: string | undefined): string[] {
    const raw = (value || '').trim()
    const match = raw.match(/^(.+?)\s*\(([^)]+)\)\s*$/)

    if (!match) return raw ? [raw] : []

    return [match[1].trim(), match[2].trim()].filter(Boolean)
}

function findIndicationConfig(initialDiseaseArea: string) {
    const normalizedDisease = normalizeSearchLabel(initialDiseaseArea)

    if (!normalizedDisease) return null

    return pubmedIndicationConfig.indications.find((indication) => {
        const candidates = [indication.label, ...(indication.aliases || []), ...(indication.defaultDiseaseTerms || [])]

        return candidates.some((candidate) => normalizeSearchLabel(candidate) === normalizedDisease)
    })
}

function findMatchingInterventionTerms(indicationConfig: any, initialInterventions: string): string[] {
    const normalizedProduct = normalizeSearchLabel(initialInterventions)

    if (!indicationConfig) return [] // Switching this so that Otrevia is NOT pre-populated for demos

    const match = indicationConfig.interventions.find((intervention: any) => {
        const candidates = [
            intervention.id,
            intervention.label,
            intervention.primaryTerm,
            ...(intervention.terms || []),
        ]

        return candidates.some((candidate) => normalizeSearchLabel(candidate) === normalizedProduct)
    })

    if (match?.terms?.length) {
        return match.terms
    }

    const fallbackIds = indicationConfig.demoFallbackInterventionIds || []

    if (fallbackIds.length) {
        return indicationConfig.interventions
            .filter((intervention: any) => fallbackIds.includes(intervention.id))
            .flatMap((intervention: any) => intervention.terms || [])
    }

    return initialInterventions ? [initialInterventions] : []
}

const PubMedSearch: React.FC<PubMedSearchProps> = ({
    onSearchComplete,
    onLoadingChange,
    disabled = false,
    initialDiseaseArea = '',
    initialInterventions = '',
}) => {
    const { getTokenSilently } = useAuth0()
    const [diseaseArea, setDiseaseArea] = useState(initialDiseaseArea)
    const [interventions, setInterventions] = useState(initialInterventions)
    const [sponsor, setSponsor] = useState('')
    const [outcomes, setOutcomes] = useState('')
    const [authors, setAuthors] = useState('')
    const [startDate, setStartDate] = useState<Dayjs | null>(null)
    const [endDate, setEndDate] = useState<Dayjs | null>(null)

    // Article type state
    const [articleTypes, setArticleTypes] = useState<PubMedSearchArticleTypes>({
        booksAndDocuments: false,
        clinicalTrial: false,
        metaAnalysis: false,
        randomizedControlledTrial: false,
        review: false,
        systematicReview: false,
    })

    const selectedArticleTypeKeys = Object.keys(articleTypes).filter(
        (key) => articleTypes[key as keyof typeof articleTypes]
    )

    const [openAccessOnly, setOpenAccessOnly] = useState(true)
    const [retmax, setRetmax] = useState<number | ''>(500)
    const [queryString, setQueryString] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const hasHydratedFromConfig = useRef(false)

    useEffect(() => {
        if (hasHydratedFromConfig.current) return

        const indicationConfig = findIndicationConfig(initialDiseaseArea)

        // Priority order:
        // 1. Use curated disease terms from pubmedIndicationConfig when available.
        // 2. Otherwise attempt to split roadmap labels that follow
        //    "Disease Name (ACRONYM)" formatting.
        // 3. Fall back to the raw indication string.

        const configuredDiseaseTerms = indicationConfig?.defaultDiseaseTerms?.length
            ? indicationConfig.defaultDiseaseTerms // Curated disease-specific search terms
            : splitLabelWithParentheticalAcronym(initialDiseaseArea) // Generic acronym fallback

        const configuredDiseaseArea = joinTerms(configuredDiseaseTerms)
        const configuredOutcomeTerms = joinTerms(indicationConfig?.defaultOutcomeTerms || [])
        const configuredInterventionTerms = findMatchingInterventionTerms(indicationConfig, initialInterventions)

        if (configuredDiseaseArea) {
            setDiseaseArea(configuredDiseaseArea)
        }

        if (configuredOutcomeTerms) {
            setOutcomes(configuredOutcomeTerms)
        }

        if (configuredInterventionTerms.length) {
            setInterventions(joinTerms(configuredInterventionTerms))
        }

        hasHydratedFromConfig.current = true
    }, [initialDiseaseArea, initialInterventions])

    // Auto-update the query when form fields change, but user can also edit it directly
    useEffect(() => {
        const built = buildPubMedQuery({
            diseaseArea,
            interventions,
            sponsor,
            outcomes,
            authors,
            startDate,
            endDate,
            articleTypes,
            openAccessOnly,
        })
        setQueryString(built)
    }, [diseaseArea, interventions, sponsor, outcomes, authors, startDate, endDate, articleTypes, openAccessOnly])

    const handleSearch = async () => {
        const effectiveRetmax = retmax === '' ? 500 : retmax
        const params: PubMedSearchParams = {
            diseaseArea,
            interventions,
            sponsor,
            outcomes,
            authors,
            startDate,
            endDate,
            articleTypes,
            openAccessOnly,
            retmax: effectiveRetmax,
        }

        setIsLoading(true)
        onLoadingChange?.(true)
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const { ids } = await searchPubMedIds(queryString, token, effectiveRetmax)
            onSearchComplete(ids, params)
        } catch (error) {
            console.error('Error searching PubMed:', error)
        } finally {
            setIsLoading(false)
            onLoadingChange?.(false)
        }
    }

    return (
        <Grid
            container
            direction="column"
            spacing={2}
            wrap="nowrap"
            sx={{
                width: '100%',
                maxWidth: 350,
                pr: 2,
                borderRight: '1px solid #E0E0E0',
            }}
        >
            <Grid item>
                <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Search Criteria
                    <Tooltip
                        title={
                            <span style={{ fontSize: '0.85rem' }}>
                                Separate search words/strings in the Search Criteria box with a &apos;,&apos; or
                                &apos;;&apos; to produce an OR in your final search query. For example, &apos;Hereditary
                                transthyretin-mediated amyloidosis, hATTR&apos; in the Disease Area/Conditions box
                                becomes &apos;Hereditary transthyretin-mediated amyloidosis&apos;[tiab] OR
                                hATTR[tiab]&apos; within the PubMed Query builder. Some tags like [tiab] or [ad] are
                                automatically included to improve search quality but can be removed in the PubMed Query
                                box. Alternatively, other tags (e.g., [ti] for Title) can be added immediately following
                                the word in the Search Criteria box.
                            </span>
                        }
                        placement="right"
                        arrow
                    >
                        <IconButton size="small" sx={{ p: 0 }}>
                            <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: '1.3rem' }} />
                        </IconButton>
                    </Tooltip>
                </Typography>

                <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <TextField
                            label="Disease Area / Conditions"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={diseaseArea}
                            onChange={(e) => setDiseaseArea(e.target.value)}
                        />
                    </Grid>

                    <Grid item>
                        <TextField
                            label="Interventions"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={interventions}
                            onChange={(e) => setInterventions(e.target.value)}
                        />
                    </Grid>

                    <Grid item>
                        <TextField
                            label="Sponsor"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={sponsor}
                            onChange={(e) => setSponsor(e.target.value)}
                        />
                    </Grid>

                    <Grid item>
                        <LocalizationProvider dateAdapter={AdapterDayjs as any}>
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <DatePicker
                                        label="Start Month"
                                        views={['year', 'month']}
                                        value={startDate}
                                        onChange={(newValue) => setStartDate(newValue)}
                                        format="MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <DatePicker
                                        label="End Month"
                                        views={['year', 'month']}
                                        value={endDate}
                                        onChange={(newValue) => setEndDate(newValue)}
                                        format="MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </LocalizationProvider>
                    </Grid>

                    {/* Article Type Dropdown */}
                    <Grid item>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="article-type-label">Article Type</InputLabel>
                            <Select
                                labelId="article-type-label"
                                multiple
                                value={selectedArticleTypeKeys}
                                onChange={(e) => {
                                    const value = e.target.value as string[]
                                    setArticleTypes((prev) => {
                                        const next = { ...prev } as typeof prev
                                        Object.keys(next).forEach((k) => {
                                            ;(next as any)[k] = value.includes(k)
                                        })
                                        return next
                                    })
                                }}
                                input={<OutlinedInput label="Article Type" />}
                                renderValue={(selected) => {
                                    if (!selected || (Array.isArray(selected) && selected.length === 0))
                                        return '' as any
                                    const keys = Array.isArray(selected) ? selected : [selected as any]
                                    return `${keys.length} selected` as any
                                }}
                                SelectDisplayProps={{
                                    style: {
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'clip',
                                        display: 'block',
                                    },
                                }}
                                sx={{
                                    '& .MuiSelect-select': {
                                        whiteSpace: 'nowrap !important',
                                        overflow: 'hidden !important',
                                        textOverflow: 'clip !important',
                                        display: 'block !important',
                                    },
                                }}
                            >
                                {Object.keys(articleTypes).map((key) => {
                                    const typeMap: { [key: string]: string } = {
                                        booksAndDocuments: 'Books and Documents',
                                        clinicalTrial: 'Clinical Trial',
                                        metaAnalysis: 'Meta-Analysis',
                                        randomizedControlledTrial: 'Randomized Controlled Trial',
                                        review: 'Review',
                                        systematicReview: 'Systematic Review',
                                    }
                                    return (
                                        <MenuItem key={key} value={key}>
                                            <Checkbox checked={articleTypes[key as keyof typeof articleTypes]} />
                                            <ListItemText primary={typeMap[key]} />
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item>
                        <TextField
                            label="Outcomes"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={outcomes}
                            onChange={(e) => setOutcomes(e.target.value)}
                        />
                    </Grid>

                    <Grid item>
                        <TextField
                            label="Authors"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={authors}
                            onChange={(e) => setAuthors(e.target.value)}
                        />
                    </Grid>

                    <Grid item>
                        <TextField
                            label="PubMed Query"
                            variant="outlined"
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            maxRows={5}
                            value={queryString}
                            onChange={(e) => setQueryString(e.target.value)}
                            InputProps={{
                                sx: { fontFamily: 'monospace', fontSize: '0.75rem' },
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item>
                <Grid container alignItems="center" spacing={2} wrap="nowrap">
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={openAccessOnly}
                                    onChange={(e) => setOpenAccessOnly(e.target.checked)}
                                    size="small"
                                    color="success"
                                />
                            }
                            label={<Typography variant="body2">PMC full text only</Typography>}
                        />
                    </Grid>
                    <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                            label="Max results"
                            type="number"
                            variant="outlined"
                            size="small"
                            value={retmax}
                            onChange={(e) => {
                                const raw = e.target.value
                                if (raw === '') {
                                    setRetmax('')
                                    return
                                }
                                const num = Number(raw)
                                if (Number.isNaN(num)) return
                                setRetmax(Math.min(1000, Math.max(1, num)))
                            }}
                            onBlur={() => {
                                if (retmax === '') setRetmax(500)
                            }}
                            inputProps={{ min: 1, max: 1000 }}
                            sx={{ width: 110 }}
                        />
                        <Tooltip
                            title="Higher max returned article counts will take longer to process."
                            placement="right"
                            arrow
                        >
                            <IconButton size="small" sx={{ p: 0 }}>
                                <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: '1.3rem' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleSearch}
                    disabled={isLoading || disabled}
                    startIcon={isLoading || disabled ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {isLoading ? 'Searching…' : disabled ? 'Ranking…' : 'Search'}
                </Button>
            </Grid>
        </Grid>
    )
}

export default PubMedSearch
